import { useEffect, useState } from 'react';
import './css/Associacao.css';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import { useNavigate } from 'react-router-dom';
import LoadingModal from './components/modals/LoadingModal';
import Pagination10 from './components/Pagination10';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import ConfirmModal from './components/modals/ConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import usePagination10 from './components/hooks/usePagination10';
import PageCard from './components/PageCard';
import AssociacaoModal from './components/modals/AssociacaoModal';
import usePermissions from './components/hooks/usePermissions';

// Certifique-se de que o CSS para .full-screen-div ainda está sendo importado
// .full-screen-div { display: flex; flex-direction: column; min-height: 100vh; }

function Associacoes() {
  // Verificação de permissões
  const { podeAbrir, podeCriar, podeEditar, podeExcluir, podeExportar, carregando: carregandoPermissoes } = usePermissions('associacoes');
  
  const [Associacoes, setAssociacoes] = useState([]);
  const [filtroGeral, setFiltroGeral] = useState('');
  const [atualizar, setAtualizar] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showModalEditar, setShowModalEditar] = useState(false);

  const token = localStorage.getItem('token');
  const [isLoading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();

  useEffect(() => {
    if (!token) {
      navigate('/');
    }
    
    // Não carregar dados se não pode abrir o módulo
    if (!carregandoPermissoes && !podeAbrir) {
      return;
    }
    
    setLoading(true);
    axios
      .get(`${API_BASE_URL.ASSOCIACAO}/all`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(result => {
        console.log('Dados recebidos:', result.data);
        setAssociacoes(result.data);
      })
      .catch(error => {
        console.error('Erro ao obter associacoes:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [atualizar, token, navigate, podeAbrir, carregandoPermissoes]);

  const handleSelectRecord = record => {
    const recordKey = `${record.cnpj}`;

    if (selectedRecord && selectedRecord.key === recordKey) {
      setSelectedRecord(null);
    } else {
      setSelectedRecord({ key: recordKey, data: record });
    }
  };

  const getSelectedRecord = () => {
    return selectedRecord ? selectedRecord.data : null;
  };

  const hasSelectedRecord = selectedRecord !== null;

  // Função para lidar com a exclusão
  function handleDelete(c) {
    if (!c) return;

    axios
      .get(`${API_BASE_URL.REL_ASSOCIACAO_USUARIO}/exists_cnpj/` + c.cnpj, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(result => {
        if (result.data.exists) {
          showDefaultModal(
            'error',
            'Esta Associacao/Entidade possui vínculos e não pode ser excluído. Exclua os vínculos antes de tentar novamente.'
          );
        } else {
          axios
            .delete(`${API_BASE_URL.ASSOCIACAO}/delete/` + c.cnpj, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            })
            .then(() => {
              setAtualizar(prev => !prev);
              setSelectedRecord(null);
            });
        }
      })
      .catch(error => {
        console.error('Erro ao verificar vínculos:', error);
        showDefaultModal('error', 'Erro ao excluir o registro!');
        setTimeout(() => navigate('/'), 0);
      });
  }

  const handleDeleteSelected = () => {
    const record = getSelectedRecord();
    if (record) {
      showConfirmModal('delete', () => handleDelete(record));
    }
  };

  const handleNovoAssociacao = () => {
    setSelectedRecord(null);
    setShowModalEditar(true);
  };

  const handleEditSelected = () => {
    const record = getSelectedRecord();
    if (record) {
      setShowModalEditar(true);
    }
  };

  const handleModalClose = () => {
    setShowModalEditar(false);
  };

  const handleSalvarAssociacao = () => {
    setAtualizar(prev => !prev);
    handleModalClose();
    setSelectedRecord(null);
  };

  // Função para filtrar as associações com base na busca
  const normalizeString = str => {
    return str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase();
  };

  const associacoesFiltrados = Array.isArray(Associacoes)
    ? Associacoes.filter(c => {
        if (!filtroGeral) return true;
        const normalizedBusca = normalizeString(filtroGeral);
        return (
          normalizeString(c.nome || '').includes(normalizedBusca) ||
          normalizeString(c.cnpj || '').includes(normalizedBusca) ||
          normalizeString(c.logrCidade || '').includes(normalizedBusca) ||
          normalizeString(c.logrUf || '').includes(normalizedBusca)
        );
      })
    : [];

  // Paginação dos associacoes filtrados
  const totalItems = associacoesFiltrados.length;
  const { setPagination } = usePagination10(totalItems);
  const selectedAssociacoes = associacoesFiltrados.slice(
    setPagination.startIndex,
    setPagination.startIndex + setPagination.rowsPerPage
  );

  // Verificar se ainda está carregando permissões
  if (carregandoPermissoes || isLoading) {
    return <LoadingModal loading={true} />;
  }

  return (
    <PageCard
      title="Associações"
      icon="bi-building"
      scrollable={false}
      footer={!podeAbrir ? null : (totalItems > 0 ? <Pagination10 {...setPagination} /> : null)}
    >
      {!podeAbrir ? (
        <div className="alert alert-warning m-3">
          <i className="bi bi-exclamation-triangle me-2"></i>
          <strong>Acesso Negado</strong>
          <p className="mb-0 mt-2">
            Você não tem permissão para acessar o módulo de Associações.
          </p>
          <p className="mb-0 mt-2">
            <small>Entre em contato com o administrador do sistema.</small>
          </p>
          <button 
            className="btn btn-sm btn-outline-secondary mt-3"
            onClick={() => navigate('/home')}
          >
            <i className="bi bi-arrow-left me-1"></i>
            Voltar
          </button>
        </div>
      ) : (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

        <div className="d-flex flex-wrap gap-1 mb-2 p-2 bg-light rounded border align-items-center">
          <button
            className="btn btn-success btn-sm d-inline-flex align-items-center"
            onClick={handleNovoAssociacao}
            disabled={!podeCriar}
            title={!podeCriar ? 'Você não tem permissão para criar associações' : 'Criar nova associação'}
          >
            <i className="bi bi-plus-lg me-1" />
            Novo
          </button>
          <button
            className="btn btn-primary btn-sm d-inline-flex align-items-center"
            onClick={handleEditSelected}
            disabled={!podeEditar || !hasSelectedRecord}
            title={!podeEditar ? 'Você não tem permissão para editar' : !hasSelectedRecord ? 'Selecione um registro' : 'Editar registro selecionado'}
          >
            <i className="bi bi-pencil me-1" />
            Editar
          </button>
          <div className="vr mx-1" />
          <button
            className="btn btn-danger btn-sm d-inline-flex align-items-center"
            onClick={handleDeleteSelected}
            disabled={!podeExcluir || !hasSelectedRecord}
            title={!podeExcluir ? 'Você não tem permissão para excluir' : !hasSelectedRecord ? 'Selecione um registro' : 'Excluir registro selecionado'}
          >
            <i className="bi bi-trash me-1" />
            Excluir
          </button>

          <div className="ms-auto d-flex align-items-center">
            <div className="input-group" style={{ maxWidth: 300, height: 31 }}>
              <span className="input-group-text bg-white border-end-0 px-2" style={{ height: 31 }}>
                <i className="bi bi-search text-muted" style={{ fontSize: 13 }} />
              </span>
              <input
                type="text"
                className="form-control form-control-sm border-start-0"
                style={{ textTransform: 'uppercase', height: 31, fontSize: 13 }}
                placeholder="Pesquisar..."
                value={filtroGeral}
                onChange={e => setFiltroGeral(e.target.value.toUpperCase())}
              />
              {filtroGeral && (
                <button
                  className="btn btn-outline-secondary btn-sm border-start-0 d-flex align-items-center px-2"
                  style={{ height: 31 }}
                  onClick={() => setFiltroGeral('')}
                  title="Limpar pesquisa"
                  tabIndex={-1}
                >
                  <i className="bi bi-x-lg" style={{ fontSize: 11 }} />
                </button>
              )}
            </div>
          </div>
        </div>

        <div
          className="table-responsive-sm shadow-sm mb-0 small"
          style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
        >
          <table className="table table-hover table-striped table-sm">
            <thead className="table-secondary sticky-top bg-light">
              <tr>
                <th
                  className="px-2 align-middle d-none d-md-table-cell"
                  style={{ width: '15%' }}
                >
                  CNPJ
                </th>
                <th className="px-2 align-middle">Nome</th>
                <th className="px-2 align-middle" style={{ width: '20%' }}>
                  Cidade
                </th>
                <th className="px-2 align-middle" style={{ width: '8%' }}>
                  UF
                </th>
                <th className="px-2 align-middle text-center" style={{ width: '12%' }}>
                  Transparência
                </th>
              </tr>
            </thead>
            <tbody>
              {!isLoading &&
                selectedAssociacoes.map(c => {
                  const recordKey = `${c.cnpj}`;
                  const isSelected =
                    selectedRecord && selectedRecord.key === recordKey;

                  let rowClass = '';
                  let rowStyle = {
                    cursor: 'pointer',
                  };

                  if (isSelected) {
                    rowClass = 'table-active border-primary border-2';
                    rowStyle.backgroundColor = '#e3f2fd';
                    rowStyle.borderLeft = '4px solid #0d6efd';
                    rowStyle.boxShadow = '0 2px 4px rgba(13, 110, 253, 0.2)';
                  }

                  return (
                    <tr
                      key={recordKey}
                      className={rowClass}
                      style={rowStyle}
                      onClick={() => handleSelectRecord(c)}
                    >
                      <td className="px-2 d-none d-md-table-cell">{c.cnpj}</td>
                      <td className="px-2">{c.nome}</td>
                      <td className="px-2">{c.logrCidade}</td>
                      <td className="px-2">{c.logrUf}</td>
                      <td className="px-2 text-center">
                        {c.transparenciaPublica ? (
                          <span
                            className="badge bg-success-subtle text-success-emphasis"
                            title="Exibindo no Portal da Transparência"
                          >
                            <i className="bi bi-eye-fill me-1"></i>Pública
                          </span>
                        ) : (
                          <span className="badge bg-secondary-subtle text-secondary-emphasis">
                            <i className="bi bi-eye-slash-fill me-1"></i>Privada
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              {!isLoading && associacoesFiltrados.length === 0 && (
                <tr>
                  <td colSpan="5" className="text-center">
                    Nenhum registro encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      <ConfirmModal {...setConfirmModal} />
      <DefaultModal {...setDefaultModal} />

      <AssociacaoModal
        show={showModalEditar}
        onClose={handleModalClose}
        onSaveSuccess={handleSalvarAssociacao}
        associacao={getSelectedRecord()}
        isEditing={!!getSelectedRecord()}
      />
    </PageCard>
  );
}

export default Associacoes;
